import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useHistory, useLocation, Link } from "react-router-dom";
import styled from "styled-components";
import Navbar from "../components/Navbar";
import BackBar from "../components/Ticketing/BackBar";
import { apiConfirmTicketOrder } from "../api/ticketingApi";
import { createUserSocket } from "../utils/userSocket";

/**
 * Where the customer lands after paying for a ticket (MoMo, or the
 * Flutterwave redirect for Card and Airtel). It watches the order until the
 * ticket exists, then opens it.
 *
 * ONE QUESTION: POST /api/ticketing/orders/:id/confirm answers ISSUED, PENDING
 * or FAILED, and for a MoMo order the server asks MTN about THIS order's
 * payment itself. This page used to call GET /momo/:clientId first, which
 * hung for 30 seconds on every call on the server and asked about the
 * customer's newest payment rather than this order's (15 Oct 2026).
 *
 * THREE WAYS TO HEAR IT: the socket (`payment:<clientId>`, "paymentStatusUpdate"
 * and "ticketsIssued"), a sequential poll (3s for 90s, then 10s, one request
 * at a time) and a check whenever the tab becomes visible again.
 *
 * NEVER A DEAD END: after two minutes it says it is slower than usual and
 * keeps watching, so a late approval still opens the ticket here. Only the
 * server's FAILED (MTN's own answer) is shown as a failure.
 */
const FAST_POLL_MS = 3000;
const SLOW_POLL_MS = 10000;
const FAST_FOR_MS = 90 * 1000;
const SLOW_AFTER_MS = 120 * 1000;
const STOP_POLLING_AFTER_MS = 15 * 60 * 1000;

const TicketPaymentReturnPage = () => {
  const { orderId } = useParams();
  const history = useHistory();
  const query = new URLSearchParams(useLocation().search);
  const clientId = query.get("clientId");
  const [status, setStatus] = useState("processing"); // processing | issued | failed
  const [message, setMessage] = useState("");
  const [slow, setSlow] = useState(false);
  const [pollingStopped, setPollingStopped] = useState(false);
  const doneRef = useRef(false);
  const inFlightRef = useRef(false);
  const againRef = useRef(false);
  const aliveRef = useRef(true);
  const openTimerRef = useRef(null);

  // Leaving the page cancels the pending jump to the ticket, and nothing is
  // asked after it has gone.
  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
      if (openTimerRef.current) clearTimeout(openTimerRef.current);
    };
  }, []);

  const checkNow = useCallback(async () => {
    if (doneRef.current || !orderId || !aliveRef.current) return;
    if (inFlightRef.current) {
      againRef.current = true;
      return;
    }
    inFlightRef.current = true;
    try {
      const conf = await apiConfirmTicketOrder(orderId);
      if (doneRef.current || !aliveRef.current) return;
      if (conf?.status === "ISSUED") {
        doneRef.current = true;
        setStatus("issued");
        const tickets = Array.isArray(conf.tickets) ? conf.tickets : [];
        const first = tickets[0]?._id;
        openTimerRef.current = setTimeout(
          () => history.push(tickets.length === 1 && first ? `/ticket/${first}` : "/tickets"),
          900
        );
      } else if (conf?.status === "FAILED") {
        doneRef.current = true;
        setMessage(conf.message || "");
        setStatus("failed");
      }
    } catch {
      /* PENDING or a blip: keep watching */
    } finally {
      inFlightRef.current = false;
      if (againRef.current && !doneRef.current && aliveRef.current) {
        againRef.current = false;
        checkNow();
      }
    }
  }, [orderId, history]);

  // Sequential poll: fast, then slow, then stop after 15 minutes.
  useEffect(() => {
    let timer = null;
    let cancelled = false;
    const startedAt = Date.now();
    const loop = async () => {
      if (cancelled || doneRef.current) return;
      await checkNow();
      if (cancelled || doneRef.current) return;
      const elapsed = Date.now() - startedAt;
      if (elapsed >= SLOW_AFTER_MS) setSlow(true);
      if (elapsed >= STOP_POLLING_AFTER_MS) {
        setPollingStopped(true);
        return;
      }
      timer = setTimeout(loop, elapsed < FAST_FOR_MS ? FAST_POLL_MS : SLOW_POLL_MS);
    };
    loop();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [checkNow]);

  // Socket: the server says the moment the payment settles or the ticket exists.
  useEffect(() => {
    if (!clientId || !orderId) return undefined;
    // Carries the customer's token: their payment room needs it.
    const socket = createUserSocket();
    const mine = (p) => Boolean(p?.orderId) && String(p.orderId) === String(orderId);
    socket.on("connect", () => {
      socket.emit("join", `payment:${clientId}`);
      checkNow();
    });
    socket.on("paymentStatusUpdate", (p) => mine(p) && checkNow());
    socket.on("ticketsIssued", (p) => mine(p) && checkNow());
    return () => {
      socket.removeAllListeners();
      socket.disconnect();
    };
  }, [clientId, orderId, checkNow]);

  // Back on the tab (after approving the prompt on the phone): ask at once.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible") checkNow();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [checkNow]);

  return (
    <>
      <Navbar />
      <Page>
        <BackBar label="My tickets" fallback="/tickets" />
        <Card>
          {status === "processing" && !slow && (
            <>
              <Spinner />
              <h2>Confirming your payment…</h2>
              <Muted>Approve the payment on your phone if you have not yet. Your ticket opens here as soon as it is confirmed.</Muted>
            </>
          )}
          {status === "processing" && slow && (
            <>
              {!pollingStopped && <Spinner />}
              <h2>Still confirming</h2>
              <Muted>
                {pollingStopped
                  ? "We have not heard back about this payment yet. If you approved it, your ticket will be in My Tickets. Do not pay again."
                  : "This is taking longer than usual. If you approved the payment, your ticket will open here by itself. You can also find it in My Tickets. Do not pay again."}
              </Muted>
              <Link to="/tickets"><Btn>Go to My Tickets</Btn></Link>
              {pollingStopped && <LinkBtn type="button" onClick={checkNow}>Check again</LinkBtn>}
            </>
          )}
          {status === "issued" && (
            <>
              <Big $ok>Paid</Big>
              <h2>Payment confirmed</h2>
              <Muted>Taking you to your ticket…</Muted>
            </>
          )}
          {status === "failed" && (
            <>
              <Big>Payment not completed</Big>
              <Muted>{message || "The payment did not go through. You have not been charged. You can book again from My Tickets or the events page."}</Muted>
              <Link to="/tickets"><Btn>Go to My Tickets</Btn></Link>
            </>
          )}
        </Card>
      </Page>
    </>
  );
};

const Page = styled.div`max-width:460px;margin:0 auto;padding:calc(var(--lusuku-nav-h,130px) + 24px) 16px 60px;min-height:60vh;`;
const Card = styled.div`background:#fff;border:1px solid #eef0f2;border-radius:16px;padding:32px 24px;text-align:center;box-shadow:0 4px 20px rgba(0,0,0,.06);h2{font-size:20px;font-weight:800;color:#1f2937;margin:14px 0 8px;}`;
const Muted = styled.div`color:#6b7280;font-size:13.5px;line-height:1.5;`;
const Spinner = styled.div`width:38px;height:38px;border-radius:50%;border:3px solid #e5e7eb;border-top-color:#83b524;margin:0 auto;animation:spin .9s linear infinite;@keyframes spin{to{transform:rotate(360deg);}}`;
const Big = styled.div`font-size:22px;font-weight:800;color:${(p)=>(p.$ok?"#4d7a10":"#dc2626")};`;
const Btn = styled.div`display:inline-block;margin-top:18px;background:#83b524;color:#fff;border-radius:10px;padding:12px 20px;font-weight:700;`;
const LinkBtn = styled.button`display:block;margin:12px auto 0;background:none;border:0;color:#4d7a10;font-weight:700;font-size:14px;cursor:pointer;`;

export default TicketPaymentReturnPage;
